"use client";

import React from "react";
import {PageCompetitionBookSchema} from "@/sanity/pages/PageCompetitionBookTemplate";

import Image from "next/image";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import prize1 from "@/public/competition-book/prize1.png";
import prize2 from "@/public/competition-book/prize2.png";
import prize3 from "@/public/competition-book/prize3.png";

const prizeItems = [
  {
    border: "border-t-[2px] border-t-[#F7AD07]",
    place: "1st",
    image: prize1,
  },
  {
    border: "border-t-[2px] border-t-[#D8D8D8]",
    place: "2nd",
    image: prize2,
  },
  {
    border: "border-t-[2px] border-t-[#BB701A]",
    place: "3rd",
    image: prize3,
  }
]

export default function PrizesSection({
  data,
  className,
}: {
  data: PageCompetitionBookSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip py-[13rem] lg:py-[22rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        {data?.prizesTitle && (
          <div className="richtext prose-h2:text-white prose-h2:font-ivy prose-h2:italic prose-h2:mb-0 prose-h2:pb-[1rem] prose-p:text-white/70 mb-[4rem] lg:mb-[5rem]">
            <PortableText
              value={data.prizesTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.prizesCards && (
          <div className="w-[min(183rem,100%)] flex flex-col items-center lg:items-stretch lg:grid lg:grid-cols-3 gap-[2rem]">
            {data?.prizesCards.map((card, index) => (
              <div
                key={index}
                className={cn(
                  "w-full sm:w-[64rem] lg:w-auto bg-[#112628] flex flex-col items-center justify-between gap-[3rem]",
                  prizeItems[index % prizeItems.length].border
                )}
              >
                <div className="w-full flex flex-col">
                  <div className="relative w-full">
                    <Image
                      className="!w-full"
                      src={prizeItems[index % prizeItems.length].image}
                      alt=""
                    />

                    <p className="mb-0 leading-none absolute bottom-[2rem] left-[2.5rem] sm:left-[3rem] font-ivy text-[7rem] text-white/[.15] italic">
                      {prizeItems[index % prizeItems.length].place}
                    </p>
                  </div>

                  <div className="richtext richtext-start px-[2.5rem] sm:px-[3rem]">
                    <h3 className="text-white text-[4rem] lg:text-[4rem] !font-ivy !font-normal">
                      {card.title}
                    </h3>

                    <p className="text-white/70 font-[400]">
                      {card.text}
                    </p>

                    <hr className="w-full border-t-white/10 my-[2rem]" />

                    <ul className="flex flex-col gap-[1rem] pl-[2.5rem]">
                      {card?.list && card?.list?.length && card?.list?.map((item, listIndex) => (
                        <li
                          key={listIndex}
                          className="m-0 text-white/70 font-normal"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="w-full px-[2.5rem] sm:px-[3rem] pb-[3rem] flex flex-col gap-[.5rem]">
                  <p className="mb-0 leading-none text-white/70 font-[400] font-geistmono ps-1">WERT</p>

                  <div className="flex items-end gap-[1rem]">
                    <p className={cn(
                      "text-[4.75rem] lg:text-[5rem] leading-none mb-0",
                      index === 0 ? "text-[#D9F9B9]" : "text-[#D8D8D8]"
                    )}>
                      {card.value}
                    </p>
                    <p className="leading-none mb-0 text-white/60 text-[1.75rem] lg:text-[2rem] pb-[.25rem]">
                      {card.valueSmallText}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="w-full sm:w-[64rem] lg:w-[min(183rem,100%)] p-[2.5rem] sm:p-[3rem] bg-[#112628] border-[2px] border-[#112628]/20 mt-[2rem]">
          {data?.prizesBottomText && (
            <div className="richtext richtext-start prose-headings:w-full prose-headings:mb-[2rem] prose-headings:pb-[2rem] prose-headings:border-b prose-headings:border-b-white/10 prose-headings:text-white prose-headings:text-[4rem] prose-headings:!font-ivy prose-headings:font-light prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-light prose-li:text-white/70 prose-ul:mb-[2rem] prose-ul:flex prose-ul:flex-col prose-ul:gap-[1rem] prose-ul:pl-[2.5rem] prose-li:m-0">
              <PortableText
                value={data.prizesBottomText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {data?.prizesCTA && (
          <CTACornerButton
            className="[&_button]:!text-[2rem] lg:[&_button]:!text-[2.25rem] mt-[2rem]"
            data={data.prizesCTA}
          />
        )}
      </div>
    </section>
  );
}
